"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface WireDiameterHeroCopy {
  categoryBadge: string;
  heading: string;
  lead: string;
  secondaryLabel: string;
  imageAlt: string;
  chipLabel: string;
  cardNote: string;
  cardLink: string;
  specStrip: Array<{
    label: string;
    val: string;
  }>;
}
const copyData: Record<Lang, WireDiameterHeroCopy> = {
  en: {
    categoryBadge: "Woven Wire Mesh Engineering",
    heading: "Wire Diameter & Open Area",
    lead: "Wire diameter and aperture size govern open area ratio, determining the engineering balance between structural burst strength and clean differential pressure. MutualFilter manufactures precision stainless steel wire mesh calibrated to ASTM E2016 and ISO 9044 standards.",
    secondaryLabel: "Mesh & Aperture Data",
    imageAlt: "Calibrated stainless steel woven wire mesh showing wire diameter and open area aperture geometry",
    chipLabel: "Plain & Twilled Weaves",
    cardNote: "Precision optical calibration",
    cardLink: "Delta P Models",
    specStrip: [{
      label: "Wire Diameter (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "Aperture Range (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "Mesh Count Range",
      val: "1 – 635 Mesh"
    }, {
      label: "Compliance Standard",
      val: "ISO 9044 / ASTM E2016"
    }]
  },
  zh: {
    categoryBadge: "编织丝网工程规范",
    heading: "丝径与开孔率",
    lead: "金属丝径与网孔孔径共同决定有效流通开孔率，在机械抗压承载力与流体初始压降之间形成核心工程权衡。MutualFilter 生产符合 ASTM E2016 与 ISO 9044 标准的精密不锈钢编织丝网，支持定向参数定制。",
    secondaryLabel: "目数与孔径规格表",
    imageAlt: "展示丝径与开孔几何结构的不锈钢编织金属丝网微观特写",
    chipLabel: "平纹与斜纹编织结构",
    cardNote: "光学精密标定检测",
    cardLink: "压降模型",
    specStrip: [{
      label: "常用丝径范围 (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "孔径公称范围 (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "编织目数范围",
      val: "1 – 635 目 (Mesh)"
    }, {
      label: "制造公差标准",
      val: "ISO 9044 / ASTM E2016"
    }]
  },
  es: {
    categoryBadge: "Ingeniería de malla tejida",
    heading: "Diámetro de hilo y área abierta",
    lead: "El diámetro del alambre y el tamaño de la abertura determinan la proporción de área abierta, equilibrando la resistencia mecánica a la rotura y la caída de presión limpia. MutualFilter fabrica mallas de acero inoxidable conforme a ASTM E2016 e ISO 9044.",
    secondaryLabel: "Datos de malla y apertura",
    imageAlt: "Malla metálica tejida de acero inoxidable con geometría calibrada de hilo y área abierta",
    chipLabel: "Tejidos lisos y cruzados",
    cardNote: "Calibración óptica de precisión",
    cardLink: "Modelos Delta P",
    specStrip: [{
      label: "Diámetro de hilo (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "Rango de apertura (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "Rango de malla",
      val: "1 – 635 Mesh"
    }, {
      label: "Norma de conformidad",
      val: "ISO 9044 / ASTM E2016"
    }]
  },
  de: {
    categoryBadge: "Drahtgewebe-Konstruktion",
    heading: "Drahtdurchmesser & offene Siebfläche",
    lead: "Drahtdurchmesser und Maschenweite bestimmen den freien Siebflächenanteil und damit das Gleichgewicht zwischen mechanischer Berstfestigkeit und Durchströmungswiderstand. MutualFilter fertigt Edelstahlgewebe nach ASTM E2016 und ISO 9044.",
    secondaryLabel: "Maschen- & Öffnungsdaten",
    imageAlt: "Präzises Edelstahldrahtgewebe mit kalibrierter Drahtstärke und Siebflächengeometrie",
    chipLabel: "Glatte & Köperbindungen",
    cardNote: "Optische Präzisionskalibrierung",
    cardLink: "Delta-P-Modelle",
    specStrip: [{
      label: "Drahtdurchmesser (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "Öffnungsbereich (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "Maschenbereich",
      val: "1 – 635 Mesh"
    }, {
      label: "Normenkonformität",
      val: "ISO 9044 / ASTM E2016"
    }]
  },
  fr: {
    categoryBadge: "Ingénierie toile métallique",
    heading: "Diamètre de fil et surface ouverte",
    lead: "Le diamètre du fil et la taille d'ouverture régissent le pourcentage de vide de maille, établissant l'équilibre entre résistance mécanique et perte de charge. MutualFilter fabrique des toiles en acier inoxydable selon ASTM E2016 et ISO 9044.",
    secondaryLabel: "Données mesh et ouvertures",
    imageAlt: "Toile tissée en acier inoxydable illustrant le diamètre de fil et la surface ouverte",
    chipLabel: "Tissages unis et croisés",
    cardNote: "Étalonnage optique de précision",
    cardLink: "Modèles Delta P",
    specStrip: [{
      label: "Diamètre de fil (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "Plage d'ouverture (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "Gamme de mesh",
      val: "1 – 635 Mesh"
    }, {
      label: "Conformité normative",
      val: "ISO 9044 / ASTM E2016"
    }]
  },
  pt: {
    categoryBadge: "Engenharia de malha tecida",
    heading: "Diâmetro de fio e área aberta",
    lead: "O diâmetro do fio e a abertura governam a proporção de área aberta, determinando o equilíbrio entre resistência mecânica e perda de carga inicial. A MutualFilter fabrica telas de aço inoxidável em conformidade com ASTM E2016 e ISO 9044.",
    secondaryLabel: "Dados de malha e abertura",
    imageAlt: "Tela de arame tecido de aço inoxidável mostrando calibre de fio e área aberta",
    chipLabel: "Telas lisas e cruzadas",
    cardNote: "Calibração óptica de precisão",
    cardLink: "Modelos Delta P",
    specStrip: [{
      label: "Diâmetro de fio (d)",
      val: "0.020 – 2.00 mm"
    }, {
      label: "Faixa de abertura (w)",
      val: "20 µm – 25 mm"
    }, {
      label: "Faixa de malha",
      val: "1 – 635 Mesh"
    }, {
      label: "Padrão de conformidade",
      val: "ISO 9044 / ASTM E2016"
    }]
  }
};
export default function WireDiameterHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="wirediameterhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly two badges: category (bg-card) + standard (bg-muted) */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                {copy.categoryBadge}
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                ISO 9044 / ASTM E2016
              </Badge>
            </div>

            {/* Main Single H1 */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.heading}
            </h1>

            {/* Lead paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* Two CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/MeshCountAndAperturePage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.secondaryLabel}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column (5 cols on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-thick-and-thin-5acf17.png" alt={copy.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.chipLabel}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.cardNote}</span>
                  </div>
                  <Link to="/DifferentialPressureDeltaPModelsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip under grid */}
        <div className="mf-spec-strip">
          {copy.specStrip.map((item, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{item.label}</span>
              <span className="mf-spec-strip__value">{item.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}